import { win } from "./pd-window.js";
import { updateUI } from "../marketo-form-to-rest-api-v2";
import {
  trackFormDataSaved,
  trackFormHidden,
  trackFormPrefilled,
} from "../segment-tracks";
import { cookie } from "./cookies.js";

/** @typedef {{ type?: string, value?: string}} formDataElement */
/** @typedef {{ [key: string]: formDataElement }} formDataType */

// Map of possible field names and their types
/** @type {{ [key: string]: { names: string[], type: string } }} */
const fieldNamesMap = {
  firstName: { names: ["FirstName"], type: "default" },
  lastName: { names: ["LastName"], type: "default" },
  name: { names: ["name"], type: "default" },
  email: { names: ["Email", "email"], type: "default" },
  phone: { names: ["Phone"], type: "default" },
  company: { names: ["Company", "company"], type: "default" },
  jobTitle: { names: ["Title"], type: "default" },
  country: { names: ["Form_Country__c"], type: "default" },
  state: { names: ["Form_State__c"], type: "default" },
  internationalMktgOptIn: {
    names: ["internationalMarketingOptIn"],
    type: "radio",
  },
};

/**
 * Gets the form type based on the form action
 * @param {HTMLElement} form
 * @returns {string | null} - Returns the form type or null if it's not a known form type
 */
const getFormType = (form) => {
  if (!form) return null;

  const formAction = form.getAttribute("action") ?? "";

  const formTypes = {
    mkto: "Marketo Form",
    salesforce: "Salesforce Form",
  };

  for (const [key, value] of Object.entries(formTypes)) {
    if (formAction.includes(key)) {
      return value;
    }
  }

  return null;
};

/**
 * Saves the form data to local storage
 * @param {formDataType} formData
 * @param {string | null} formType
 * @returns {boolean}
 * */
function saveFormDataToLocalStorage(formData, formType) {
  const ls = window.localStorage;
  if (!ls) return false;

  try {
    ls.setItem("formData", JSON.stringify(formData));
    ls.setItem("formDataTimestamp", new Date().valueOf().toString());
  } catch (e) {
    win.Bugsnag?.notify(e);
    return false;
  }

  // Track form data saved
  trackFormDataSaved(formType, formData);
  return true;
}

/**
 * Removes form data and its timestamp from local storage
 *
 * @param {Storage} [ls=window.localStorage] - The local storage object to remove data from. Defaults to window.localStorage
 * @returns {boolean} - Returns true if the data was successfully removed, false otherwise
 */
const removeFormDataFromLocalStorage = (ls = window.localStorage) => {
  if (!ls) return false;

  ls.removeItem("formData");
  ls.removeItem("formDataTimestamp");
  return true;
};

/**
 * Gets the form data from local storage
 * @returns {formDataType | false} - Returns the form data or false if it doesn't exist or is stale
 * */
function getFormDataFromLocalStorage() {
  const ls = window.localStorage;
  if (!ls) return false;

  const formData = ls.getItem("formData");
  if (!formData) return false;

  const formDataTimestamp = ls.getItem("formDataTimestamp");
  if (!formDataTimestamp) {
    removeFormDataFromLocalStorage(ls);
    return false;
  }

  // Check if the form data is stale
  const maxHours = 9; // Max age of the form data in hours
  const maxAge = 1000 * 60 * 60 * maxHours;
  const now = new Date().valueOf();
  const timeDiff = now - parseInt(formDataTimestamp);

  // Remove the form data if it's stale
  if (timeDiff > maxAge) {
    removeFormDataFromLocalStorage(ls);
    return false;
  }

  const parsedFormData = JSON.parse(formData);

  if (!parsedFormData || typeof parsedFormData !== "object") return false;

  return parsedFormData;
}

/**
 * Gets form data from URL query parameters
 * @returns {formDataType}
 */
function getFormDataFromQueryParams() {
  /** @type {formDataType} */
  const queryParamData = {};

  const urlParams = new URLSearchParams(window.location.search);

  Object.keys(fieldNamesMap).forEach((fieldKey) => {
    const paramValue = urlParams.get(fieldKey);
    const fieldConfig = fieldNamesMap[fieldKey];
    if (paramValue && fieldConfig) {
      const stringValue = String(paramValue).trim();
      if (stringValue) {
        queryParamData[fieldKey] = {
          type: fieldConfig.type,
          value: stringValue,
        };
      }
    }
  });

  return queryParamData;
}

/**
 * Saves form data locally
 *
 * @param {HTMLElement} form - The form element
 * @returns {boolean}
 */
export const saveFormDataLocally = (form) => {
  if (!form) return false;

  const formType = getFormType(form);

  /** @type {formDataType} */
  const parsedFormData = Object.keys(fieldNamesMap).reduce((data, key) => {
    if (!fieldNamesMap[key]) return data;

    // Check if any of the possible field names exists in the form
    for (const fieldName of fieldNamesMap[key].names) {
      const fieldElement = form?.querySelector(`[name=${fieldName}]`);

      // Skip if the field is not found or is disabled
      if (!fieldElement || fieldElement.disabled) continue;

      /** @type formDataElement */
      let newElement = {};

      // If field is a radio button, get the checked value
      if (fieldElement.type === "radio") {
        const checkedRadio = form.querySelector(`[name=${fieldName}]:checked`);
        if (checkedRadio && checkedRadio instanceof HTMLInputElement) {
          newElement["type"] = "radio";
          newElement["value"] = checkedRadio.value;
        }
        // Else, get the value of the field
      } else {
        const fieldValue = fieldElement.value.trim();
        if (fieldValue) {
          newElement["type"] = "default";
          newElement["value"] = fieldValue;
        }
      }

      return newElement ? { ...data, [key]: newElement } : data;
    }
    return data;
  }, {});

  // Add single name field if both first and last name are present
  if (parsedFormData["firstName"] && parsedFormData["lastName"]) {
    parsedFormData["name"] = {
      type: "default",
      value: `${parsedFormData["firstName"].value} ${parsedFormData["lastName"].value}`,
    };
  }

  // Get the current saved form data
  const currentSavedFormData = getFormDataFromLocalStorage() || {};

  // Check if the form data is the same as the current saved form data
  if (JSON.stringify(currentSavedFormData) === JSON.stringify(parsedFormData)) {
    return true;
  }

  // Merge the current saved form data with the new form data (overwriting existing values)
  const mergedFormData = { ...currentSavedFormData, ...parsedFormData };

  return saveFormDataToLocalStorage(mergedFormData, formType);
};

/**
 * @param {HTMLFormElement} form
 * @param {string[]} requiredFields
 * @returns {boolean}
 * */
const hasFields = (form, requiredFields = []) => {
  if (!form) return false;

  const formData = new FormData(form);

  return requiredFields.every(
    (reqField) =>
      fieldNamesMap[reqField] &&
      fieldNamesMap[reqField].names.some((name) => formData.get(name)),
  );
};

/**
 * Determines if a form is hideable based on the presence of required fields and its validity.
 *
 * @param {HTMLFormElement} form - The form element to check.
 * @param {string[]} requiredFieldsToSubmit - An array of field names that are required to submit the form.
 * @returns {boolean} - Returns true if the form is hideable, false otherwise.
 */
const isFormHideable = (form, requiredFieldsToSubmit) => {
  if (!form) return false;

  if (!hasFields(form, requiredFieldsToSubmit)) return false;

  return form?.checkValidity();
};

const deleteFormDataCookieExists = () => {
  // Find cookie "deleteFormData"
  const deleteFormDataCookie = cookie("deleteFormData");

  // Remove cookie and form data
  if (deleteFormDataCookie === "1") {
    document.cookie = `deleteFormData=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    removeFormDataFromLocalStorage();
    return true;
  }
  return false;
};

/**
 * Pre-fills a form with saved form data from query parameters and local storage
 *
 * @param {HTMLFormElement} form - The form element to prefill
 * @returns {boolean} - Returns true if at least one element of the form is prefilled
 */
export const prefillForm = (form) => {
  if (!form) return false;

  if (deleteFormDataCookieExists()) return false;

  // Get data from query parameters first
  const queryParamData = getFormDataFromQueryParams();

  // Get data from local storage
  const savedFormData = getFormDataFromLocalStorage() || {};

  // Merge data: query parameters take priority over local storage
  const mergedFormData = { ...savedFormData, ...queryParamData };

  // If no data is available from either source, return false
  if (Object.keys(mergedFormData).length === 0) return false;

  /** @type {string[]} */
  let prefilledFields = [];

  Object.entries(mergedFormData).forEach(([fieldKey, fieldValue]) => {
    let fieldElement;
    let fieldName;

    if (!fieldNamesMap[fieldKey]) return;

    // Check if any of the possible field names exists in the form
    for (const name of fieldNamesMap[fieldKey].names) {
      fieldElement = form?.querySelector(`[name=${name}]`);
      fieldName = name;
      if (fieldElement) break;
    }

    if (!fieldElement) return;

    // If field is a radio button, check the option with the saved value
    if (fieldValue.type === "radio") {
      const checkedElement = form?.querySelector(`[name=${fieldName}]:checked`);
      // Prefill the field if it's not checked already
      if (!checkedElement && fieldValue.value) {
        const radioElement = form?.querySelector(
          `[name=${fieldName}][value='${fieldValue.value}']`,
        );
        if (radioElement && radioElement instanceof HTMLInputElement) {
          radioElement.checked = true;
          if (!radioElement.checkValidity()) {
            radioElement.checked = false;
            return;
          }
          prefilledFields.push(fieldKey);
        }
      }
      // Else, fill the field with the saved value
    } else if (fieldValue.value && !fieldElement.value) {
      fieldElement.value = fieldValue.value.trim();
      if (!fieldElement.checkValidity()) {
        fieldElement.value = "";
        return;
      }
      // For Marketo forms only:
      // If field is the country field, update the UI to show/hide state and consent fields
      if (fieldKey === "country") {
        updateUI(fieldValue.value);
      }
      prefilledFields.push(fieldKey);
    }
  });

  // Track form prefill
  if (prefilledFields.length > 0) {
    const formType = getFormType(form);
    trackFormPrefilled(formType, prefilledFields);
  }

  return prefilledFields.length > 0;
};

/**
 * Validates a form before submitting
 *
 * @param {Event} evt
 * @param {HTMLFormElement} form - The form to be validated
 * @param {HTMLElement | null} elementToBeHidden - The element to be hidden if the form is invalid
 */
const validateFormBeforeSubmit = (evt, form, elementToBeHidden) => {
  evt.preventDefault();
  if (!form.checkValidity()) {
    elementToBeHidden?.classList.remove("hidden");
    form.reportValidity();
    return;
  }

  // Resume form submission by dispatching a new submit event
  const submitEvent = new Event("submit", {
    bubbles: true,
    cancelable: true,
  });
  form.dispatchEvent(submitEvent);
};

/**
 * Pre-fills a form and hides an element if the form is valid
 * @param {HTMLElement} form - The form element to be prefilled
 * @param {HTMLElement | null} elementToBeHidden - The element to be hidden if the form is valid
 * @param {string[]} requiredFieldsToSubmit - The required fields to submit the form
 */
export const prefillAndHideForm = (
  form,
  elementToBeHidden,
  requiredFieldsToSubmit = [],
) => {
  if (!form || !(form instanceof HTMLFormElement)) return;

  // Pre-fill form
  const isPrefilled = prefillForm(form);

  // Hide form if it's prefilled and hideable
  if (isPrefilled && isFormHideable(form, requiredFieldsToSubmit)) {
    elementToBeHidden?.classList.add("hidden");

    // Add event listener to show form if it's invalid on submission
    const submitButton = form.querySelector("[type=submit]");
    if (submitButton) {
      submitButton.addEventListener("click", (evt) =>
        validateFormBeforeSubmit(evt, form, elementToBeHidden),
      );
    }

    const formType = getFormType(form);
    // Track form hidden
    trackFormHidden(formType);
  }
};
